import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Copy, X, Leaf } from "lucide-react";

// Elegant shareable scripture card. Renders to a hidden canvas via SVG → PNG for download.
export const ScriptureCard = ({ scripture, open, onOpenChange }) => {
    const cardRef = useRef(null);
    const [downloading, setDownloading] = useState(false);

    if (!scripture) return null;

    const download = async () => {
        setDownloading(true);
        try {
            const svg = cardRef.current?.querySelector("svg");
            if (!svg) throw new Error("no svg");
            const xml = new XMLSerializer().serializeToString(svg);
            const svg64 = btoa(unescape(encodeURIComponent(xml)));
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = 1080; canvas.height = 1350;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, 1080, 1350);
                canvas.toBlob((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `eden-scripture-${scripture.reference.replace(/[^a-z0-9]/gi, "-").toLowerCase()}.png`;
                    a.click();
                    URL.revokeObjectURL(url);
                    setDownloading(false);
                }, "image/png");
            };
            img.onerror = () => { setDownloading(false); toast.error("Couldn't render image."); };
            img.src = "data:image/svg+xml;base64," + svg64;
        } catch {
            setDownloading(false);
            toast.error("Download failed.");
        }
    };

    const copyText = async () => {
        const text = `"${scripture.verse}"\n— ${scripture.reference} (KJV)\n\n${scripture.reflection}\n\nShared from EDEN — tend the garden entrusted to you.`;
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied to clipboard.");
        } catch { toast.error("Copy failed."); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl bg-background border border-foreground/15 rounded-md p-0 overflow-hidden">
                <DialogHeader className="p-6 pb-0">
                    <DialogTitle className="font-serif text-2xl">Share this verse</DialogTitle>
                </DialogHeader>
                <div className="p-6" ref={cardRef}>
                    {/* SVG so it exports cleanly */}
                    <svg viewBox="0 0 1080 1350" className="w-full h-auto rounded-md" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0" stopColor="#F8F5EE" />
                                <stop offset="1" stopColor="#EDE6D6" />
                            </linearGradient>
                            <pattern id="dots" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                                <circle cx="1" cy="1" r="0.6" fill="#1F3A2E" fillOpacity="0.06" />
                            </pattern>
                        </defs>
                        <rect width="1080" height="1350" fill="url(#bg)" />
                        <rect width="1080" height="1350" fill="url(#dots)" />
                        {/* border */}
                        <rect x="60" y="60" width="960" height="1230" fill="none" stroke="#1F3A2E" strokeOpacity="0.15" strokeWidth="1" />
                        {/* Header */}
                        <g fontFamily="Inter, sans-serif" fill="#1F3A2E">
                            <text x="120" y="150" fontSize="20" letterSpacing="8" opacity="0.55">SCRIPTURE OF THE DAY · KJV</text>
                        </g>
                        {/* Verse (wrapped) */}
                        <g fontFamily="Cormorant Garamond, serif" fill="#1F3A2E">
                            <VerseText text={`\u201C${scripture.verse}\u201D`} x={120} y={310} maxWidth={840} lineHeight={82} fontSize={68} />
                        </g>
                        {/* Reference */}
                        <g fontFamily="Inter, sans-serif" fill="#C6A15B">
                            <text x="120" y="960" fontSize="24" letterSpacing="6">
                                {scripture.reference.toUpperCase()}
                            </text>
                        </g>
                        {/* Divider */}
                        <line x1="120" y1="1000" x2="240" y2="1000" stroke="#C6A15B" strokeWidth="2" />
                        {/* Reflection */}
                        <g fontFamily="Inter, sans-serif" fill="#1F3A2E" opacity="0.75">
                            <VerseText text={scripture.reflection} x={120} y={1060} maxWidth={840} lineHeight={34} fontSize={22} maxLines={3} />
                        </g>
                        {/* Footer */}
                        <g fontFamily="Inter, sans-serif" fill="#1F3A2E" opacity="0.7">
                            <text x="120" y="1240" fontSize="20" letterSpacing="6">EDEN · TEND THE GARDEN ENTRUSTED TO YOU.</text>
                        </g>
                    </svg>
                </div>
                <div className="px-6 pb-6 flex flex-wrap gap-2">
                    <Button onClick={download} disabled={downloading} data-testid="scripture-download-btn" className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md">
                        <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> {downloading ? "Preparing…" : "Download PNG"}
                    </Button>
                    <Button onClick={copyText} variant="outline" data-testid="scripture-copy-btn" className="rounded-md border-foreground/15">
                        <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} /> Copy text
                    </Button>
                    <Button onClick={() => onOpenChange(false)} variant="ghost" className="ml-auto">
                        <X className="w-4 h-4 mr-2" strokeWidth={1.5} /> Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Simple word-wrap SVG text component
const VerseText = ({ text, x, y, maxWidth, lineHeight, fontSize, maxLines }) => {
    // Character budget per line ~ maxWidth / (fontSize * 0.42)
    const budget = Math.floor(maxWidth / (fontSize * 0.44));
    const words = (text || "").split(/\s+/);
    const lines = [];
    let cur = "";
    for (const w of words) {
        if ((cur + " " + w).trim().length <= budget) {
            cur = (cur + " " + w).trim();
        } else {
            if (cur) lines.push(cur);
            cur = w;
        }
    }
    if (cur) lines.push(cur);
    const clipped = maxLines ? lines.slice(0, maxLines) : lines;
    if (maxLines && lines.length > maxLines) clipped[clipped.length - 1] += "…";
    return (
        <text x={x} y={y} fontSize={fontSize}>
            {clipped.map((l, i) => (
                <tspan key={i} x={x} dy={i === 0 ? 0 : lineHeight}>{l}</tspan>
            ))}
        </text>
    );
};

export default ScriptureCard;
